import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { FiImage, FiFileText, FiX } from "react-icons/fi";
import { httpService } from "../../services/httpService";
import config from "../../config";

const s3Base = config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL || "";

const toDisplayUrl = (key) => {
  if (!key) return "";
  return key.startsWith("http") ? key : `${s3Base}/${key}`;
};

const formatDateTime = (dateString) => {
  if (!dateString) return "";
  try {
    return new Date(dateString).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const GroomingSummaryPreview = ({ refId, serviceType = "grooming" }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [activeTab, setActiveTab] = useState("notes");

  const [checkinPhotos, setCheckinPhotos] = useState([]);
  const [checkinNotes, setCheckinNotes] = useState("");
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    if (!refId) return;

    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        const [checkinRes, activitiesRes] = await Promise.allSettled([
          // skipToast: true -> a missing check-in is a valid empty state here,
          // not an error, so we don't want the global toast firing for it.
          httpService.get(`/checkin/${serviceType}/${refId}`, { skipToast: true }),
          httpService.get(`/serviceactivity/${serviceType}/${refId}/activities`),
        ]);

        if (!isMounted) return;

        const checkinData =
          checkinRes.status === "fulfilled"
            ? checkinRes.value?.data?.checkin_data
            : null;

        const activitiesRaw =
          activitiesRes.status === "fulfilled"
            ? activitiesRes.value?.data?.logs ?? []
            : [];

        const sorted = Array.isArray(activitiesRaw)
          ? [...activitiesRaw].sort(
              (a, b) => new Date(a.logged_at) - new Date(b.logged_at)
            )
          : [];

        setCheckinPhotos(checkinData?.photos || []);
        setCheckinNotes(checkinData?.notes || "");
        setActivities(sorted);
      } catch (err) {
        console.error("Failed to load grooming summary preview:", err);
        if (isMounted) setError("Failed to load notes & photos");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [refId, serviceType]);

  const summary = useMemo(() => {
    const notesList = [
      ...(checkinNotes ? [{ stage: "Check-in", note: checkinNotes }] : []),
      ...activities
        .filter((a) => a?.description?.trim())
        .map((a) => ({
          stage: "Activity",
          note: a.description,
          date: formatDateTime(a.logged_at),
        })),
    ];

    const photosList = [
      ...checkinPhotos.map((url) => ({ url, stage: "Check-in" })),
      ...activities.flatMap((a) =>
        (a.photos || []).map((url) => ({
          url,
          stage: "Activity",
          note: a.description,
          date: formatDateTime(a.logged_at),
        }))
      ),
    ];

    return { notesList, photosList };
  }, [checkinPhotos, checkinNotes, activities]);

  if (!refId) return null;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
        <span className="w-3.5 h-3.5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        Loading notes & photos...
      </div>
    );
  }

  if (error) {
    return <p className="text-xs text-red-500 py-1">{error}</p>;
  }

  const isEmpty =
    checkinPhotos.length === 0 && !checkinNotes && activities.length === 0;

  if (isEmpty) {
    return (
      <p className="text-xs text-gray-400 italic py-1">
        No notes or photos recorded
      </p>
    );
  }

  return (
    <div>
      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-0.5 rounded-lg w-max mb-2">
        <button
          type="button"
          onClick={() => setActiveTab("notes")}
          className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
            activeTab === "notes"
              ? "bg-white text-indigo-700 shadow-sm"
              : "text-gray-500 hover:text-gray-800"
          }`}
        >
          <FiFileText className="w-3 h-3" />
          Notes
          <span className="ml-0.5 bg-indigo-100 text-indigo-700 px-1.5 rounded-full text-[10px]">
            {summary.notesList.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("photos")}
          className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
            activeTab === "photos"
              ? "bg-white text-indigo-700 shadow-sm"
              : "text-gray-500 hover:text-gray-800"
          }`}
        >
          <FiImage className="w-3 h-3" />
          Photos
          <span className="ml-0.5 bg-indigo-100 text-indigo-700 px-1.5 rounded-full text-[10px]">
            {summary.photosList.length}
          </span>
        </button>
      </div>

      {/* TAB: Notes */}
      {activeTab === "notes" && (
        <div className="space-y-1.5">
          {summary.notesList.length === 0 ? (
            <p className="text-xs text-gray-400 italic py-1">No notes recorded</p>
          ) : (
            summary.notesList.map((n, idx) => (
              <div
                key={idx}
                className="text-xs bg-slate-50 border border-slate-100 rounded-lg px-2.5 py-1.5"
              >
                <span className="font-medium text-indigo-600 mr-1">
                  {n.stage}:
                </span>
                <span className="text-gray-700">{n.note}</span>
              </div>
            ))
          )}
        </div>
      )}

      {/* TAB: Photos */}
      {activeTab === "photos" && (
        <div className="flex flex-wrap gap-2">
          {summary.photosList.length === 0 ? (
            <p className="text-xs text-gray-400 italic py-1">No photos recorded</p>
          ) : (
            summary.photosList.map((photo, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setPreviewPhoto(toDisplayUrl(photo.url))}
                className="shrink-0 w-16 h-16 rounded-lg border border-gray-200 overflow-hidden hover:ring-2 hover:ring-indigo-300 transition"
              >
                <img
                  src={toDisplayUrl(photo.url)}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </button>
            ))
          )}
        </div>
      )}

      {/* Full screen photo preview */}
      {previewPhoto &&
        createPortal(
          <div
            className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-6"
            onClick={() => setPreviewPhoto(null)}
          >
            <img
              src={previewPhoto}
              alt=""
              className="max-w-full max-h-full rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              onClick={() => setPreviewPhoto(null)}
              className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default React.memo(GroomingSummaryPreview);